import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CRMHeader from './crm-modules/header';
import { DragDropContext } from '@hello-pangea/dnd';
import Column from './crm-modules/Column';
import { AlertBox } from '../../components/ui/alertBox';
import AddNewColumnModel from './crm-modules/AddNewColumnModel';
import AddNewInvestorWindow from './crm-modules/addNewInvestorWindow';
import LoadingSpinner from './../../components/ui/LoadingSpinner';
import { useCRM } from '../../hooks/crm';
import { fetchInvestorStatsById } from '../../hooks/crm/api';
import CRMBoardViewToolbar from './crm-modules/CRMBoardViewToolbar';
import {
  applyBoardView,
  buildBoardViewOptions,
  hasBoardViewFilters,
  sanitizeBoardViewFilters,
} from './crm-modules/crmBoardView';

const EMPTY_VIEW_FILTERS = {
  countries: [],
  types: [],
  industries: [],
};

const CRM = () => {
  const { t } = useTranslation();
  const {
    boards,
    selectedBoard,
    selectedBoardId,
    selectedBoardColumns,
    loading,
    refreshing,
    moveInvestor,
    addColumn,
  } = useCRM();

  const [isInvestorModalOpen, setIsInvestorModalOpen] = useState(false);
  const [selectedColumnId, setSelectedColumnId] = useState(null);
  const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);
  const [isSavingColumn, setIsSavingColumn] = useState(false);
  const [viewFilters, setViewFilters] = useState(EMPTY_VIEW_FILTERS);
  const [sortMode, setSortMode] = useState('manual');
  const [investorStatsMap, setInvestorStatsMap] = useState({});
  const [isLoadingRecentActivity, setIsLoadingRecentActivity] = useState(false);

  const pendingStatsRef = useRef(new Set());

  const handleOpenInvestorModal = useCallback((columnId) => {
    setSelectedColumnId(columnId);
    setIsInvestorModalOpen(true);
  }, []);

  const handleCloseInvestorModal = useCallback(() => {
    setIsInvestorModalOpen(false);
    setSelectedColumnId(null);
  }, []);

  const handleSaveNewColumn = useCallback(async (columnName) => {
    if (!selectedBoardId) {
      return;
    }

    setIsSavingColumn(true);

    try {
      await addColumn(selectedBoardId, columnName);
      setIsAddColumnModalOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingColumn(false);
    }
  }, [addColumn, selectedBoardId]);

  const onDragEnd = useCallback(async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const [, investorId] = draggableId.split('-');

    try {
      await moveInvestor({
        boardId: selectedBoardId,
        sourceColumnId: parseInt(source.droppableId, 10),
        destinationColumnId: parseInt(destination.droppableId, 10),
        investorId,
        destinationIndex: destination.index,
      });
    } catch (error) {
      console.error('Update failed:', error);
    }
  }, [moveInvestor, selectedBoardId]);

  useEffect(() => {
    setViewFilters(EMPTY_VIEW_FILTERS);
    setSortMode('manual');
  }, [selectedBoardId]);

  const boardViewOptions = useMemo(
    () => buildBoardViewOptions(selectedBoardColumns),
    [selectedBoardColumns]
  );

  useEffect(() => {
    setViewFilters((previousFilters) => {
      const nextFilters = sanitizeBoardViewFilters(previousFilters, boardViewOptions);

      if (
        nextFilters.countries.join('|') === previousFilters.countries.join('|') &&
        nextFilters.types.join('|') === previousFilters.types.join('|') &&
        nextFilters.industries.join('|') === previousFilters.industries.join('|')
      ) {
        return previousFilters;
      }

      return nextFilters;
    });
  }, [boardViewOptions]);

  const selectedBoardInvestorIds = useMemo(
    () => [...new Set(
      selectedBoardColumns.flatMap((column) =>
        (column?.investors || []).map((investor) => String(investor?.investor_id || '')).filter(Boolean)
      )
    )],
    [selectedBoardColumns]
  );

  useEffect(() => {
    if (sortMode !== 'recentActivity' || selectedBoardInvestorIds.length === 0) {
      return undefined;
    }

    const missingInvestorIds = selectedBoardInvestorIds.filter(
      (investorId) => !investorStatsMap[investorId] && !pendingStatsRef.current.has(investorId)
    );

    if (!missingInvestorIds.length) {
      return undefined;
    }

    let cancelled = false;

    missingInvestorIds.forEach((investorId) => {
      pendingStatsRef.current.add(investorId);
    });
    setIsLoadingRecentActivity(true);

    Promise.all(
      missingInvestorIds.map(async (investorId) => [investorId, await fetchInvestorStatsById(investorId)])
    )
      .then((entries) => {
        if (cancelled) {
          return;
        }

        setInvestorStatsMap((previousMap) => {
          const nextMap = { ...previousMap };
          entries.forEach(([investorId, stats]) => {
            nextMap[investorId] = stats;
          });
          return nextMap;
        });
      })
      .finally(() => {
        missingInvestorIds.forEach((investorId) => {
          pendingStatsRef.current.delete(investorId);
        });

        if (!cancelled) {
          setIsLoadingRecentActivity(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [investorStatsMap, selectedBoardInvestorIds, sortMode]);

  const { columns: visibleBoardColumns, visibleCount } = useMemo(
    () => applyBoardView(selectedBoardColumns, viewFilters, sortMode, investorStatsMap),
    [investorStatsMap, selectedBoardColumns, sortMode, viewFilters]
  );

  const hasActiveView = hasBoardViewFilters(viewFilters) || sortMode !== 'manual';

  const handleToggleFilter = useCallback((key, value) => {
    setViewFilters((previousFilters) => {
      const currentValues = previousFilters[key] || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((currentValue) => currentValue !== value)
        : [...currentValues, value];

      return {
        ...previousFilters,
        [key]: nextValues,
      };
    });
  }, []);

  const handleResetBoardView = useCallback(() => {
    setViewFilters(EMPTY_VIEW_FILTERS);
    setSortMode('manual');
  }, []);

  const boardContent = selectedBoard ? (
    <div className="overflow-x-auto" key={selectedBoard.board_id}>
      <CRMBoardViewToolbar
        filters={viewFilters}
        sortMode={sortMode}
        options={boardViewOptions}
        visibleCount={visibleCount}
        totalCount={boardViewOptions.totalCount}
        onToggleFilter={handleToggleFilter}
        onSortChange={setSortMode}
        onReset={handleResetBoardView}
        hasActiveView={hasActiveView}
        isLoadingRecentActivity={isLoadingRecentActivity}
        t={t}
      />

      {hasActiveView && visibleCount === 0 ? (
        <div className="mt-4">
          <AlertBox variant="yellow">{t('app:crm.t66')}</AlertBox>
        </div>
      ) : null}

      <div className="flex">
        {visibleBoardColumns.map((column) => (
          <Column
            boardId={selectedBoardId}
            key={String(column.column_id)}
            columnId={column.column_id}
            column={column}
            onAddInvestor={handleOpenInvestorModal}
            disableDnD={hasActiveView}
          />
        ))}
        <div className="flex flex-col w-64 items-center mt-4 mx-2 justify-start">
          <div className="rounded-md w-full h-full flex justify-center">
            <button
              className="cursor-pointer text-sm text-clr4 p-6 bg-stone-100 rounded-md hover:bg-stone-200 focus:outline-none"
              onClick={() => setIsAddColumnModalOpen(true)}
            >
              {t('app:crm.t2')}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex flex-col h-full min-h-screen">
      <CRMHeader />

      {loading || refreshing ? (
        <LoadingSpinner />
      ) : boards.length === 0 ? (
        <div className="mt-10 items-center mx-auto">
          <AlertBox variant="yellow">{t('app:crm.t1')}</AlertBox>
        </div>
      ) : (
        hasActiveView ? boardContent : <DragDropContext onDragEnd={onDragEnd}>{boardContent}</DragDropContext>
      )}

      <AddNewInvestorWindow
        isOpen={isInvestorModalOpen}
        onClose={handleCloseInvestorModal}
        boardId={selectedBoardId}
        columnId={selectedColumnId}
      />

      <AddNewColumnModel
        isOpen={isAddColumnModalOpen}
        onClose={() => setIsAddColumnModalOpen(false)}
        onSave={handleSaveNewColumn}
        isLoading={isSavingColumn}
      />
    </div>
  );
};

export default React.memo(CRM);
